/**
 * FiPr.ai Strip Board — Premium Cinematic Edition
 *
 * Design language: Dark luxury · Linear meets Hollywood
 * Color system: Charcoal base · Gold accent only · Scene type = left border
 */
import React, { useState } from 'react';
import {
  Lock, Unlock, X, Zap, AlertTriangle, Clock, Film, MapPin,
  ChevronDown, Plus, Sparkles,
  User, Moon, Sun, Sunrise, Sunset, Star, Layers
} from 'lucide-react';
import { mlSafeStyle } from '../../utils/mlSafe';
import SceneViewModal from '../SceneViewModal';
import { formatSceneNumber } from '../../utils/sceneNumber';

// ── Design tokens — use CSS variables for full theme support ─────────────────
const T = {
  bg:          'var(--cb-bg)',
  surface:     'var(--cb-surface)',
  card:        'var(--cb-surface-h)',
  cardHover:   'var(--cb-surface-a)',
  border:      'var(--cb-border)',
  borderHover: 'var(--cb-border)',
  accent:      'var(--cb-accent)',
  accentDim:   'var(--cb-accent-dim)',
  textPrimary: 'var(--cb-text)',
  textSecond:  'var(--cb-text-sec)',
  textDim:     'var(--cb-text-dim)',
};

// ── Scene type color system — left border only ────────────────────────────────
const SCENE_TYPES = {
  'EXT DAY':        { color: '#F5C518', icon: Sun,           label: 'EXT · DAY',   short: 'EX-D' },
  'INT DAY':        { color: '#60A5FA', icon: Film,          label: 'INT · DAY',   short: 'IN-D' },
  'EXT NIGHT':      { color: '#8B5CF6', icon: Moon,          label: 'EXT · NIGHT', short: 'EX-N' },
  'INT NIGHT':      { color: '#34D399', icon: Layers,        label: 'INT · NIGHT', short: 'IN-N' },
  'EXT DAWN':       { color: '#FB923C', icon: Sunrise,       label: 'EXT · DAWN',  short: 'EX-W' },
  'EXT DUSK':       { color: '#F97316', icon: Sunset,        label: 'EXT · DUSK',  short: 'EX-K' },
  'INT DAWN':       { color: '#FCD34D', icon: Sunrise,       label: 'INT · DAWN',  short: 'IN-W' },
  'INT DUSK':       { color: '#EA580C', icon: Sunset,        label: 'INT · DUSK',  short: 'IN-K' },
  'INT CONTINUOUS': { color: '#94A3B8', icon: Film,          label: 'INT · CONT',  short: 'IN-C' },
  'EXT CONTINUOUS': { color: '#64748B', icon: Film,          label: 'EXT · CONT',  short: 'EX-C' },
};
const FLAGGED_TYPE = { color: '#F87171', icon: AlertTriangle, label: 'FLAGGED', short: 'FX' };

const getSceneType = (scene) => {
  if (scene.is_flagged) return FLAGGED_TYPE;
  const k = `${(scene.location_type || 'INT').toUpperCase()} ${(scene.time_of_day || 'DAY').toUpperCase()}`;
  return SCENE_TYPES[k] || SCENE_TYPES['INT DAY'];
};

// ── AI insights ───────────────────────────────────────────────────────────────
function generateAIInsights(days, breakdownById) {
  const insights = [];
  if (!days.length) return insights;

  // Night shoot fatigue
  for (let i = 0; i < days.length; i++) {
    const d = days[i];
    const nightScenes = (d.scene_ids || []).filter(id => {
      const s = breakdownById[id];
      return s && (s.time_of_day || '').toUpperCase() === 'NIGHT';
    });
    if (nightScenes.length >= 3) {
      insights.push({ id: `fatigue-${d.id}`, type: 'warning', icon: Moon,
        text: `Night shoot fatigue — Day ${d.day_number}`,
        sub: `${nightScenes.length} night scenes back-to-back` });
      break;
    }
  }

  // Actor hold days
  const actorDays = {};
  days.forEach(d => {
    (d.scene_ids || []).forEach(id => {
      (breakdownById[id]?.characters_in_scene || []).forEach(a => {
        actorDays[a] = actorDays[a] || [];
        actorDays[a].push(d.day_number);
      });
    });
  });
  Object.entries(actorDays).forEach(([actor, nums]) => {
    const sorted = [...new Set(nums)].sort((a, b) => a - b);
    if (sorted.length >= 2) {
      const holds = (sorted[sorted.length - 1] - sorted[0] + 1) - sorted.length;
      if (holds >= 2) {
        insights.push({ id: `hold-${actor}`, type: 'budget', icon: User,
          text: `${actor.split(' ')[0]} idle ${holds} day${holds > 1 ? 's' : ''}`,
          sub: 'Holding fee risk — consider regrouping scenes' });
      }
    }
  });

  // Company moves
  const moveDays = days.filter(d => {
    const locs = new Set((d.scene_ids || []).map(id => breakdownById[id]?.location_name).filter(Boolean));
    return locs.size > 2;
  });
  if (moveDays.length > 0) {
    insights.push({ id: 'moves', type: 'efficiency', icon: MapPin,
      text: `${moveDays.length} day${moveDays.length > 1 ? 's' : ''} with 3+ locations`,
      sub: 'Location clustering saves company move costs' });
  }

  // Merge opportunity
  for (let i = 0; i < days.length - 1; i++) {
    const a = days[i], b = days[i + 1];
    const la = (a.location || '').toLowerCase(), lb = (b.location || '').toLowerCase();
    if (la && lb && la === lb) {
      const total = [...(a.scene_ids || []), ...(b.scene_ids || [])].reduce((s, id) => s + (breakdownById[id]?.page_count_eighths || 0), 0) / 8;
      if (total <= 10) {
        insights.push({ id: `merge-${a.id}`, type: 'save', icon: Zap,
          text: `Merge Day ${a.day_number} + ${b.day_number}`,
          sub: `Same location · ${total.toFixed(1)}p combined` });
        break;
      }
    }
  }

  return insights.slice(0, 5);
}

// ── Production Timeline ───────────────────────────────────────────────────────
const ProductionTimeline = ({ days, breakdownById, totalUnscheduled }) => {
  const totalPages = days.reduce((sum, d) =>
    sum + (d.scene_ids || []).reduce((s, id) => s + (breakdownById[id]?.page_count_eighths || 0), 0) / 8, 0);
  const totalScenes = new Set(days.flatMap(d => d.scene_ids || [])).size;

  return (
    <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}` }} className="px-6 py-4 flex-shrink-0">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-5">
          <div>
            <div className="text-[9px] font-bold uppercase mb-1" style={{ color: T.accent, letterSpacing: '0.14em' }}>
              Production Schedule
            </div>
            <div className="flex items-center gap-5">
              {[
                { v: days.length, l: 'shoot days' },
                { v: totalScenes, l: 'scenes' },
                { v: `${totalPages.toFixed(0)}p`, l: 'total pages' },
              ].map(({ v, l }) => (
                <div key={l} className="flex items-baseline gap-1.5">
                  <span className="text-xl font-bold" style={{ color: T.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{v}</span>
                  <span className="text-[10px]" style={{ color: T.textDim }}>{l}</span>
                </div>
              ))}
              {totalUnscheduled > 0 && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: T.accentDim, color: T.accent }}>
                  {totalUnscheduled} unscheduled
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Day segments */}
      <div className="flex gap-0.5 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)' }}>
        {days.map(d => {
          const pages = (d.scene_ids || []).reduce((s, id) => s + (breakdownById[id]?.page_count_eighths || 0), 0) / 8;
          const hasNight = (d.scene_ids || []).some(id => (breakdownById[id]?.time_of_day || '').toUpperCase() === 'NIGHT');
          const hasFlagged = (d.scene_ids || []).some(id => breakdownById[id]?.is_flagged);
          const maxP = Math.max(...days.map(dd => (dd.scene_ids || []).reduce((s, id) => s + (breakdownById[id]?.page_count_eighths || 0), 0) / 8), 1);
          return (
            <div key={d.id} className="flex-1 rounded-full" style={{ minWidth: 3, background: hasFlagged ? '#F87171' : hasNight ? '#8B5CF6' : T.accent, opacity: 0.35 + (pages / maxP) * 0.65 }} title={`Day ${d.day_number} · ${pages.toFixed(1)}p`} />
          );
        })}
      </div>
    </div>
  );
};

// ── AI Insights ───────────────────────────────────────────────────────────────
const AIInsightsPanel = ({ insights, onDismiss }) => {
  const cfg = { warning: { c: '#F87171', bg: 'rgba(248,113,113,0.08)' }, budget: { c: '#FB923C', bg: 'rgba(251,146,60,0.08)' }, efficiency: { c: '#60A5FA', bg: 'rgba(96,165,250,0.08)' }, save: { c: '#34D399', bg: 'rgba(52,211,153,0.08)' } };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-3 w-3" style={{ color: T.accent }} />
        <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: T.accent, letterSpacing: '0.14em' }}>AI Insights</span>
      </div>
      {insights.map(ins => {
        const c = cfg[ins.type] || cfg.efficiency;
        const Icon = ins.icon;
        return (
          <div key={ins.id} className="rounded-xl p-3 group relative" style={{ background: T.surface, border: `1px solid ${T.border}`, borderLeft: `2px solid ${c.c}` }}>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: c.bg }}>
                <Icon className="h-2.5 w-2.5" style={{ color: c.c }} />
              </div>
              <div className="flex-1 min-w-0 pr-4">
                <p className="text-xs font-semibold" style={{ color: T.textPrimary }}>{ins.text}</p>
                <p className="text-[10px] mt-0.5" style={{ color: T.textSecond }}>{ins.sub}</p>
              </div>
            </div>
            <button onClick={() => onDismiss(ins.id)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: T.textDim }}>
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

// ── Scene Card ────────────────────────────────────────────────────────────────
const SceneCard = ({ scene, dayId, onRemove, onDragStart, isLocked, onToggleLock, conflicts, onViewScene }) => {
  const [hovered, setHovered] = useState(false);
  const type = getSceneType(scene);
  const pages = ((scene.page_count_eighths || 0) / 8).toFixed(1);
  const hasConflict = conflicts.some(c => c.sceneId === scene.id);
  const cast = scene.characters_in_scene || [];
  const Icon = type.icon;

  return (
    <div
      draggable={!isLocked}
      onDragStart={e => { if (!isLocked) onDragStart(e, scene.id, dayId); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`strip-${scene.scene_number ?? 'unnumbered'}`}
      className="mb-2 rounded-xl overflow-hidden select-none transition-all duration-150"
      style={{
        background: hovered ? T.cardHover : T.card,
        border: `1px solid ${hovered ? T.borderHover : T.border}`,
        borderLeft: `3px solid ${type.color}`,
        boxShadow: hovered ? `0 6px 20px rgba(0,0,0,0.5)` : '0 1px 3px rgba(0,0,0,0.3)',
        transform: hovered ? 'translateY(-1px)' : 'none',
        cursor: isLocked ? 'not-allowed' : 'grab',
      }}
    >
      {hasConflict && <div className="h-0.5 w-full" style={{ background: '#F87171' }} />}
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-base font-bold tabular-nums" style={{ color: type.color, lineHeight: 1 }}>{formatSceneNumber(scene.scene_number, { padStart: 2 })}</span>
            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md" style={{ background: `${type.color}18` }}>
              <Icon className="h-2.5 w-2.5" style={{ color: type.color }} />
              <span className="text-[8px] font-bold uppercase" style={{ color: type.color, letterSpacing: '0.08em' }}>{type.short}</span>
            </div>
          </div>
          <div style={{ opacity: hovered ? 1 : 0, transition: 'opacity 0.15s' }} className="flex items-center gap-1">
            {/* View scene breakdown/script button */}
            {onViewScene && (
              <button
                onClick={e => { e.stopPropagation(); onViewScene(scene); }}
                title="View breakdown & script"
                className="h-5 w-5 rounded flex items-center justify-center transition-colors"
                style={{ color: T.accent }}
              >
                <Layers className="h-2.5 w-2.5" />
              </button>
            )}
            <button onClick={e => { e.stopPropagation(); onToggleLock(scene.id); }} className="h-5 w-5 rounded flex items-center justify-center" style={{ color: isLocked ? T.accent : T.textDim, background: isLocked ? T.accentDim : 'transparent' }}>
              {isLocked ? <Lock className="h-2.5 w-2.5" /> : <Unlock className="h-2.5 w-2.5" />}
            </button>
            {onRemove && (
              <button onClick={e => { e.stopPropagation(); onRemove(scene.id, dayId); }} className="h-5 w-5 rounded flex items-center justify-center hover:text-red-400 transition-colors" style={{ color: T.textDim }}>
                <X className="h-2.5 w-2.5" />
              </button>
            )}
          </div>
        </div>
        <p className="text-xs leading-snug truncate mb-2.5"
           style={{ color: T.textPrimary, ...mlSafeStyle(scene.location_name || scene.scene_header) }}>
          {scene.location_name || scene.scene_header}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Clock className="h-2.5 w-2.5" style={{ color: T.textDim }} />
            <span className="text-[10px] font-mono font-semibold" style={{ color: T.textSecond }}>{pages}p</span>
          </div>
          {cast.length > 0 && (
            <div className="flex -space-x-1">
              {cast.slice(0, 3).map((c, i) => (
                <div key={i} title={c} className="h-4 w-4 rounded-full flex items-center justify-center text-[7px] font-bold ring-1" style={{ background: `${type.color}20`, color: type.color, ringColor: T.bg }}>
                  {(c[0] || '').toUpperCase()}
                </div>
              ))}
              {cast.length > 3 && <span className="text-[9px] ml-1" style={{ color: T.textDim }}>+{cast.length - 3}</span>}
            </div>
          )}
        </div>
      </div>
      {isLocked && <div className="absolute bottom-1.5 right-1.5"><Lock className="h-2 w-2" style={{ color: T.accent, opacity: 0.6 }} /></div>}
    </div>
  );
};

// ── Day Column ────────────────────────────────────────────────────────────────
const DayColumn = ({ day, scenes, onRemove, onDragStart, onDragOver, onDrop, lockedScenes, onToggleLock, conflicts, onAddScene, allScenes, onViewScene }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addQ, setAddQ] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const totalPages = scenes.reduce((s, sc) => s + (sc.page_count_eighths || 0), 0) / 8;
  const flaggedCount = scenes.filter(s => s.is_flagged).length;
  const hasConflict = conflicts.some(c => c.dayId === day.id);
  const scheduledIds = new Set(scenes.map(s => s.id));
  const available = allScenes.filter(s => !scheduledIds.has(s.id) && (!addQ || String(s.scene_number ?? '').includes(addQ) || (s.location_name || '').toLowerCase().includes(addQ.toLowerCase())));

  return (
    <div
      className="flex-shrink-0 flex flex-col rounded-2xl overflow-hidden transition-all duration-200"
      style={{
        width: 212,
        background: T.surface,
        border: `1px solid ${dragOver ? 'rgba(232,197,71,0.25)' : T.border}`,
        borderTop: `2px solid ${hasConflict ? '#F87171' : dragOver ? T.accent : 'rgba(232,197,71,0.35)'}`,
        boxShadow: dragOver ? `0 0 0 1px rgba(232,197,71,0.15), 0 12px 40px rgba(0,0,0,0.5)` : '0 2px 8px rgba(0,0,0,0.25)',
      }}
      onDragOver={e => { e.preventDefault(); setDragOver(true); onDragOver(day.id); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { setDragOver(false); onDrop(e, day.id); }}
      data-testid={`day-column-${day.day_number}`}
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3 cursor-pointer select-none" onClick={() => setCollapsed(c => !c)}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-bold uppercase" style={{ color: T.accent, letterSpacing: '0.16em' }}>Day {String(day.day_number).padStart(2, '0')}</span>
            {hasConflict && <div className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: '#F87171' }} />}
          </div>
          <ChevronDown className="h-3 w-3 transition-transform" style={{ color: T.textDim, transform: collapsed ? 'rotate(-90deg)' : 'none' }} />
        </div>
        {day.shoot_date && (
          <div className="text-[9px] font-medium mb-1.5" style={{ color: T.textDim }}>
            {new Date(day.shoot_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).toUpperCase()}
          </div>
        )}
        {day.location && (
          <div className="flex items-center gap-1 mb-3">
            <MapPin className="h-2.5 w-2.5 flex-shrink-0" style={{ color: T.textDim }} />
            <span className="text-[11px] truncate" style={{ color: T.textSecond }}>{day.location}</span>
          </div>
        )}
        {/* Stats row */}
        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <Film className="h-2.5 w-2.5" style={{ color: T.textDim }} />
            <span className="text-[10px] font-semibold tabular-nums" style={{ color: T.textSecond }}>{scenes.length}</span>
          </div>
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <Clock className="h-2.5 w-2.5" style={{ color: T.textDim }} />
            <span className="text-[10px] font-semibold tabular-nums" style={{ color: T.textSecond }}>{totalPages.toFixed(1)}p</span>
          </div>
          {flaggedCount > 0 && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-lg" style={{ background: 'rgba(248,113,113,0.1)' }}>
              <AlertTriangle className="h-2.5 w-2.5" style={{ color: '#F87171' }} />
              <span className="text-[10px] font-semibold" style={{ color: '#F87171' }}>{flaggedCount}</span>
            </div>
          )}
        </div>
        {/* Page load bar */}
        <div className="mt-2.5 h-px rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(totalPages / 10 * 100, 100)}%`, background: totalPages > 10 ? '#F87171' : T.accent, opacity: 0.7 }} />
        </div>
      </div>

      {/* Scene cards */}
      {!collapsed && (
        <div className="flex-1 px-3 pb-2 overflow-y-auto" style={{ maxHeight: 460, minHeight: 60 }}>
          {scenes.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10" style={{ opacity: 0.25 }}>
              <div className="h-8 w-8 rounded-xl border border-dashed mb-2 flex items-center justify-center" style={{ borderColor: T.textDim }}>
                <Plus className="h-3 w-3" style={{ color: T.textDim }} />
              </div>
              <span className="text-[10px]" style={{ color: T.textDim }}>Drop scenes here</span>
            </div>
          )}
          {scenes.map(s => (
            <SceneCard key={s.id} scene={s} dayId={day.id} onRemove={onRemove} onDragStart={onDragStart} isLocked={lockedScenes.has(s.id)} onToggleLock={onToggleLock} conflicts={conflicts} onViewScene={onViewScene} />
          ))}
        </div>
      )}

      {/* Add button */}
      {!collapsed && (
        <div className="px-3 pb-3 relative flex-shrink-0">
          <button
            onClick={() => setAddOpen(o => !o)}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl transition-all duration-150"
            style={{ color: addOpen ? T.accent : T.textDim, border: `1px dashed ${addOpen ? 'rgba(232,197,71,0.35)' : T.border}`, background: addOpen ? T.accentDim : 'transparent' }}
          >
            <Plus className="h-3 w-3" />
            <span className="text-[10px] font-semibold">Add scene</span>
          </button>
          {addOpen && (
            <div className="absolute bottom-full left-0 right-0 mb-2 z-20 rounded-xl overflow-hidden shadow-2xl" style={{ background: '#1C1C24', border: `1px solid ${T.border}` }}>
              <div className="p-2">
                <input value={addQ} onChange={e => setAddQ(e.target.value)} placeholder="Search scenes…" autoFocus className="w-full text-xs px-2.5 py-1.5 rounded-lg outline-none" style={{ background: T.card, color: T.textPrimary, border: `1px solid ${T.border}` }} />
              </div>
              <div className="max-h-48 overflow-y-auto pb-1">
                {available.slice(0, 12).map(s => {
                  const t = getSceneType(s);
                  return (
                    <button key={s.id} onClick={() => { onAddScene(s.id, day.id); setAddOpen(false); setAddQ(''); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-left" style={{ color: T.textSecond }} onMouseEnter={e => e.currentTarget.style.background = T.card} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div className="h-4 w-0.5 rounded-full flex-shrink-0" style={{ background: t.color }} />
                      <span className="text-xs font-bold font-mono flex-shrink-0" style={{ color: t.color }}>{formatSceneNumber(s.scene_number, { padStart: 2 })}</span>
                      <span className="text-xs truncate" style={{ color: T.textPrimary }}>{s.location_name || s.scene_header}</span>
                    </button>
                  );
                })}
                {available.length === 0 && <p className="text-center text-[10px] py-3" style={{ color: T.textDim }}>All scenes scheduled</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Unscheduled Tray ──────────────────────────────────────────────────────────
const UnscheduledTray = ({ scenes, onDragStart }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [q, setQ] = useState('');
  const filtered = scenes.filter(s => !q || String(s.scene_number ?? '').includes(q) || (s.location_name || '').toLowerCase().includes(q.toLowerCase()));
  const byLoc = filtered.reduce((acc, s) => { const l = s.location_name || 'Unknown'; (acc[l] = acc[l] || []).push(s); return acc; }, {});

  if (collapsed) {
    return (
      <div onClick={() => setCollapsed(false)} className="flex-shrink-0 flex flex-col items-center justify-start gap-2 rounded-2xl cursor-pointer py-4 px-2" style={{ width: 40, background: T.surface, border: `1px solid ${T.border}`, borderTop: `2px solid rgba(232,197,71,0.35)` }}>
        <span className="text-[8px] font-bold uppercase" style={{ color: T.accent, writingMode: 'vertical-rl', letterSpacing: '0.12em' }}>{scenes.length} queued</span>
      </div>
    );
  }

  return (
    <div className="flex-shrink-0 flex flex-col rounded-2xl overflow-hidden" style={{ width: 204, background: T.surface, border: `1px solid ${T.border}`, borderTop: `2px solid rgba(232,197,71,0.35)` }}>
      <div className="px-4 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-bold uppercase" style={{ color: T.accent, letterSpacing: '0.14em' }}>Scene Queue</span>
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md" style={{ background: T.accentDim, color: T.accent }}>{scenes.length}</span>
          </div>
          <button onClick={() => setCollapsed(true)} style={{ color: T.textDim }}><ChevronDown className="h-3 w-3" style={{ transform: 'rotate(90deg)' }} /></button>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter scenes…" className="w-full text-xs px-2.5 py-1.5 rounded-lg outline-none" style={{ background: T.card, color: T.textPrimary, border: `1px solid ${T.border}` }} />
      </div>
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-3" style={{ maxHeight: 520 }}>
        {Object.entries(byLoc).map(([loc, locScenes]) => (
          <div key={loc}>
            <div className="text-[8px] font-bold uppercase mb-1.5 px-1" style={{ color: T.textDim, letterSpacing: '0.12em' }}>{loc}</div>
            {locScenes.map(s => {
              const t = getSceneType(s);
              return (
                <div key={s.id} className="flex items-center gap-2 px-2.5 py-2 rounded-xl mb-1 select-none transition-all duration-150" style={{ background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${t.color}`, cursor: 'grab' }} draggable onDragStart={e => onDragStart(e, s.id, null)} onMouseEnter={e => { e.currentTarget.style.background = T.cardHover; e.currentTarget.style.borderColor = T.borderHover; }} onMouseLeave={e => { e.currentTarget.style.background = T.card; e.currentTarget.style.borderColor = T.border; }}>
                  <span className="text-xs font-bold font-mono flex-shrink-0" style={{ color: t.color }}>{formatSceneNumber(s.scene_number, { padStart: 2 })}</span>
                  <span className="text-[10px] truncate flex-1" style={{ color: T.textSecond }}>{s.location_name || s.scene_header}</span>
                  <span className="text-[9px] font-mono" style={{ color: T.textDim }}>{((s.page_count_eighths || 0) / 8).toFixed(1)}</span>
                </div>
              );
            })}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10" style={{ color: T.textDim }}>
            <Star className="h-5 w-5 mb-2" style={{ opacity: 0.25 }} />
            <span className="text-[10px]">All scenes scheduled</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Legend ────────────────────────────────────────────────────────────────────
const Legend = () => (
  <div className="flex items-center gap-5 flex-wrap">
    {Object.entries(SCENE_TYPES).slice(0, 6).map(([key, t]) => (
      <div key={key} className="flex items-center gap-1.5">
        <div className="h-3 w-0.5 rounded-full" style={{ background: t.color }} />
        <span className="text-[9px] font-medium" style={{ color: T.textDim }}>{t.label}</span>
      </div>
    ))}
    <div className="flex items-center gap-1.5">
      <div className="h-3 w-0.5 rounded-full" style={{ background: FLAGGED_TYPE.color }} />
      <span className="text-[9px] font-medium" style={{ color: T.textDim }}>Flagged / FX</span>
    </div>
  </div>
);

// ── Main export ───────────────────────────────────────────────────────────────
export default function StripBoardView({ days, allScenes, breakdownById, onRemoveScene, onDragStart, onDragOver, onDrop, onAddScene, conflicts, lockedScenes, onToggleLock, projectId, token }) {
  const scheduledIds = new Set(days.flatMap(d => d.scene_ids || []));
  const unscheduled = allScenes.filter(s => !scheduledIds.has(s.id));
  const [dismissed, setDismissed] = useState(new Set());
  const [viewingScene, setViewingScene] = useState(null);
  const rawInsights = generateAIInsights(days, breakdownById);
  const insights = rawInsights.filter(i => !dismissed.has(i.id));
  const getScenesForDay = d => (d.scene_ids || []).map(id => breakdownById[id]).filter(Boolean);

  return (
    <div className="flex flex-col h-full" style={{ background: T.bg }}>
      <ProductionTimeline days={days} breakdownById={breakdownById} totalUnscheduled={unscheduled.length} />
      <div className="flex-1 flex overflow-hidden">
        {/* Scrollable board */}
        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          <div className="flex gap-3 p-5 h-full items-start" style={{ minWidth: 'max-content' }}>
            <UnscheduledTray scenes={unscheduled} onDragStart={onDragStart} />
            {days.map(d => (
              <DayColumn key={d.id} day={d} scenes={getScenesForDay(d)} onRemove={onRemoveScene} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} lockedScenes={lockedScenes} onToggleLock={onToggleLock} conflicts={conflicts} onAddScene={onAddScene} allScenes={allScenes} onViewScene={setViewingScene} />
            ))}
            {days.length === 0 && (
              <div className="flex-1 flex flex-col items-center justify-center py-32">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
                  <Film className="h-7 w-7" style={{ color: T.textDim, opacity: 0.4 }} />
                </div>
                <p className="text-sm font-medium mb-1" style={{ color: T.textSecond }}>No shoot days yet</p>
                <p className="text-xs" style={{ color: T.textDim }}>Run AI Auto-Schedule or add a day manually</p>
              </div>
            )}
          </div>
        </div>
        {/* AI panel */}
        {insights.length > 0 && (
          <div className="flex-shrink-0 w-68 p-4 overflow-y-auto border-l" style={{ borderColor: T.border, background: T.bg, width: 264 }}>
            <AIInsightsPanel insights={insights} onDismiss={id => setDismissed(p => new Set([...p, id]))} />
          </div>
        )}
      </div>
      {/* Bottom legend */}
      <div className="px-5 py-3 flex-shrink-0 border-t" style={{ borderColor: T.border, background: T.surface }}>
        <Legend />
      </div>

      {/* Scene view modal — opens Breakdown tab by default */}
      {viewingScene && projectId && (
        <SceneViewModal
          scene={viewingScene}
          projectId={projectId}
          token={token}
          defaultTab="breakdown"
          onClose={() => setViewingScene(null)}
        />
      )}
    </div>
  );
}
